import type {
  ClaimableRewardStatusEnum,
  ExternalRewardStatus,
  MissionBundleType,
  MissionStatusEnum,
  ObjectiveMissionWagerAndWinType,
  RewardStatusEnum,
  RewardsMap,
  RewardTypeEnum,
  StatusTournamentsEnum,
} from '@gamanza-engage/web-client-sdk';
import type { IncentiveRewardType } from '../../app/package/base/types';
import type { GameDetails, LobbyGame, Studio } from '../base/package/api/package/gaming/gaming-domain';
import type { BonusState, Campaign, CampaignType, ExternalConfig } from '../mod-bonus/types';

export type BonusOfferReward = RewardsMap[RewardTypeEnum.BONUS_OFFER];

export type IncentivePageView =
  | 'base'
  | 'mission'
  | 'missions'
  | 'past-missions'
  | 'past-rewards'
  | 'active-rewards'
  | 'mini-game'
  | 'reward-claim'
  | 'reward'
  | 'rewards'
  | 'tournaments'
  | 'past-tournaments'
  | 'tournament'
  | 'eligible-games';

export type IncentiveSetViewDetail =
  | IncentivePageView
  | {
      view: IncentivePageView;
      id?: string;
    };

/** Views that share a subnav — switching between them is a tab change, not deeper navigation. */
export const INCENTIVE_TAB_GROUPS: IncentivePageView[][] = [
  ['rewards', 'active-rewards', 'past-rewards'],
  ['missions', 'past-missions'],
  ['tournaments', 'past-tournaments'],
];

export type IncentiveOpenGameDetail = {
  id: string;
  source?: string;
};

export type ContributionTarget = {
  bundleId: string;
  missionId: string;
  missionState: MissionState;
  endDate?: Date;
  reward: Reward;
};

export type ContributionTournamentTarget = {
  tournamentId: string;
  endDate: Date;
  startDate: Date;
  userPosition?: number;
  points?: number;
  joinedAmount?: number;
};

export type IncentiveRewardStatus = RewardStatusEnum | ExternalRewardStatus | ClaimableRewardStatusEnum;

export type IncentiveReward = {
  gamanzaId: string;
  // updatedAt: Date;
  title: string;
  status: IncentiveRewardStatus;
  rewardString: string;
  backgroundImageUrl: string;
  games: GameDetails[] | LobbyGame[] | null;
  provider?: Studio;
  type?: CampaignType;
  campaign?: Campaign;
  campaignId: string;
  missionStateId: string;
  bundleStateId: string;
  validTo?: Date;
  validFrom?: Date;
};

export type TournamentContent = {
  startsAt: string;
  endsAt: string;
  title: string;
  tac: string;
  howTo?: string;
  pointsRules?: string;
  prizeAmounts: string[];
  prizeDescription: string;
};

export type IncentiveTournament = TournamentBase & {
  // Visuals (required for active tournaments)
  image: string;
  backgroundImage: string;

  // Timing
  isExpiringSoon: boolean;
  isLive: boolean;
  isEnded: boolean;

  // User state
  userId?: string;
  isUserJoined: boolean;
  userPosition?: number;
  userScore?: number;
  userPositionReward?: TournamentPrize;
  points?: string;

  // Ended state prize (user's won prize, not the pool)
  endedPrizeAmount?: string;
  endedPrizeDescription?: string;

  // Leaderboard & prizes
  leaderboard: LeaderboardEntry[];
  joinedAmount?: number;

  // Games
  objectiveGames: string[];
  games: GameDetails[] | LobbyGame[];
  isViewAllButtonShown: boolean;
  viewAllGamesCb?: () => void;
  provider?: Studio;
};

export type IncentiveLeaderboardData = {
  leaderboard: LeaderboardEntry[];
  joinedAmount: number;
};

export type TournamentBase = {
  id: string;
  startsAt: Date;
  endsAt: Date;
  status: StatusTournamentsEnum;
  image?: string;
  backgroundImage?: string;
  prizes?: TournamentPrize[];
  leaderboard?: LeaderboardEntry[];
  games?: (GameDetails | LobbyGame)[];
  content: TournamentContent;
};

export type IncentivePastTournament = TournamentBase & {
  points?: number;
  userPosition?: number;
  joinedAmount?: number;
};

export type TournamentPrize = {
  fromPosition: number;
  toPosition?: number;
  position: string | number;
  reward: Reward;
};

export type LeaderboardEntry = {
  isCurrentPlayer: boolean;
  id: string;
  name: string;
  points?: number;
  prize: string;
  prizeType?: IncentiveRewardType;
  position?: number;
};

export type LeaderboardResult = {
  rewardsFinal: TournamentPrize[];
  leaderboardEntries: LeaderboardEntry[];
  currentPlayer?: LeaderboardEntry;
};

export type MissionState = {
  progress: number;
  completed: number;
  remaining: number;
  total: number;
  isExpired?: boolean;
};

export type TournamentState = {
  userPosition?: number;
  points?: number;
  joinedAmount?: number;
  hasStarted: boolean;
  hasEnded: boolean;
  areResultsReady: boolean;
};

export type Reward = {
  type: IncentiveRewardType;
  amount: number;
  description: string;
  formattedValue: string;
  bonusId: string;
  /** Game IDs from the provider campaign (populated for FREE_SPIN rewards). */
  gameIds?: string[];
  rewardGameId?: string;
  rewardGameIntegration?: string;
};

export type Mission = {
  id: string;
  name: string;
  state?: MissionState;
  reward: Reward;
  status: MissionStatus;
  startDate?: string;
  endDate?: string;
  games: GameDetails[] | LobbyGame[];
  viewAllGamesCb?: () => void;
  isViewAllButtonShown: boolean;
  provider?: Studio;
  objective: ObjectiveMissionWagerAndWinType;
};

export type MissionStatus = 'OPEN' | 'PENDING' | 'ACTIVE' | 'COMPLETED' | 'ENDED';
export type Step = MissionStatus;

export type RewardTotal = { formattedValues: string[] };
export type MissionReward = { type: IncentiveRewardType; amount: number; bonusId: string };
export type OfferReward = { type: string; amount: number; bonusId: string };

export type RewardCollection = Record<string, (MissionReward | Reward | undefined)[]>;

export type MissionsBundleType = {
  originalId: string;
  title: string;
  priority: number;
  description: string;
  termsConditions: string;
  smallImage: string;
  backgroundImage: string;
  missions: Mission[];
  currentMission: Mission;
  steps: Step[];
  reward?: RewardTotal;
  completedMissionNumber?: number;
  isOptedIn: boolean;
  isUpcoming?: boolean;
  bgColor?: string;
  startDate?: string;
  startDateOriginal?: string;
  endDate?: string;
  endDateOriginal?: string;
  updatedAt?: string;
  isExpiringSoon?: boolean;
  status?: MissionStatusEnum;
  isMultiMission?: boolean;
  bundleType?: MissionBundleType;
  mapBackgroundImage?: string;
  createdAtMs: number;
  updatedAtMs: number;
  hasStarted: boolean;
};

export type MissionsBundleExpiredType = {
  originalId: string;
  title: string;
  termsConditions: string;
  missions?: Mission[];
  currentMission?: Mission;
  reward?: RewardTotal;
  isMultiMission?: boolean;
  startDate?: string;
  endedOrCompletedDate?: string;
  endedOrCompletedDateOriginal?: string;
  status?: MissionStatusEnum;
};

export const IncentiveModuleEventTypes = {
  SET_VIEW: 'set-view',
  OPEN_GAME: 'open-game',
} as const;

export type IncentiveModuleEventTypes = (typeof IncentiveModuleEventTypes)[keyof typeof IncentiveModuleEventTypes];

export type IncentiveModuleCampaign = {
  id: string;
  type: 'MONEY' | 'FREE_SPIN';
  externalConfig: ExternalConfig;
  numFreeRounds?: number;
  games: (GameDetails | LobbyGame)[];
  description: string;
  viewAllGamesCb?: () => void;
  isViewAllButtonShown: boolean;
  turnover?: number;
};

export type IncentiveModuleCampaignWithContent = IncentiveModuleCampaign & {
  title: string;
  tac: string;
};

export type IncentiveModuleReward = {
  id: string;
  rewardAmount: number;
  winningsFromFS?: number;
  type: 'MONEY' | 'FREE_SPIN';
  validFrom: string;
  validTo: string;
  content: {
    title: string;
    termsAndConditions: string;
    reward: string;
    validFrom: string;
    validTo: string;
    description: string;
    winningsFromFS?: string;
  };
};

export type IncentiveModuleActualReward = IncentiveModuleReward & {
  games: (GameDetails | LobbyGame)[];
  viewAllGamesCb?: () => void;
  isViewAllButtonShown: boolean;
  bonusOfferId: string;
  wageringProgressPct: number;
  wageringCounter: number;
  wageringTarget: number;
  remainingWager: number;
  wageringMultiplier?: number;
};

export type IncentiveModulePastReward = IncentiveModuleReward & {
  state: BonusState;
  wageringMultiplier?: number;
};

export type IncentiveRewardToClaim = {
  id: string;
  rewardAmount: number;
  rewardSource: 'tournament' | 'bundle' | 'minigame';
  type: 'MONEY' | 'FREE_SPIN';
  content: {
    title: string;
    termsAndConditions: string;
    reward: string;
    description: string;
    winningsFromFS?: string;
  };
  status: RewardStatusEnum;
  bonusOfferId: string;
  bundleStateId?: string;
  missionStateId?: string;
  tournamentId?: string;
  groupId?: string;
  tournamentPosition?: number;
  games: (GameDetails | LobbyGame)[];
};

export type IncentiveMiniGame = {
  id: string;
  groupId: string;
  status: RewardStatusEnum | ExternalRewardStatus;
  offerId: string;
  /** Backend offer name — selects the game implementation (see MINI_GAME_OFFER_NAME_5G_WHEEL). */
  offerName: string;
  uniqueIdentifier: string;
  content: {
    termsAndConditions: string;
  };
};

export type BonusListItem = IncentiveRewardToClaim | IncentiveModuleActualReward;
export type BonusList = BonusListItem[];
export type BonusListData = {
  ghosts: IncentiveRewardToClaim[];
  actuals: IncentiveModuleActualReward[];
};

export type EventDetail =
  | { type: typeof IncentiveModuleEventTypes.SET_VIEW; payload: IncentiveSetViewDetail }
  | {
      type: typeof IncentiveModuleEventTypes.OPEN_GAME;
      payload: IncentiveOpenGameDetail;
    };

export const INCENTIVE_MODULE_EVENT = 'incentive-module-event';
